/**
 * Reminder times, which are set in the player's own timezone but have to land
 * before the game day closes at midnight Pacific.
 *
 * Someone in Hawaii who asks for 10:30pm is asking for 1:30am Pacific, by which
 * point the prompt they would be reminded about has already rotated. The
 * settings screen warns rather than silently moving their choice.
 */
import { GAME_TIME_ZONE } from '../../shared/dates';

const MINUTES_PER_DAY = 24 * 60;

/**
 * An instant's wall clock in a zone, as minutes, including the calendar date.
 *
 * The date matters. Comparing only minute-past-midnight looks sufficient and is
 * not: zone differences run from about -5 hours to +22 hours relative to
 * Pacific, so any attempt to fold them into a single day wraps. Asia/Kolkata is
 * 12.5 hours ahead, and folding turned that into 11.5 hours behind, which
 * inverted every question asked of it.
 */
function zonedMinutes(zone: string, instant: Date): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: zone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(instant);

  const value = (type: string) => Number(parts.find((part) => part.type === type)?.value ?? 0);

  return (
    Date.UTC(value('year'), value('month') - 1, value('day'), value('hour'), value('minute')) /
    60_000
  );
}

/** The zone the browser is in, e.g. 'Europe/London'. */
export function browserTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || GAME_TIME_ZONE;
  } catch {
    return GAME_TIME_ZONE;
  }
}

/**
 * How far ahead of Pacific a zone currently is, in minutes. Negative when behind.
 *
 * Derived from the two clocks rather than from a fixed table, so it is correct
 * across daylight saving on either side without special cases.
 */
export function minutesAheadOfPacific(zone: string, instant: Date = new Date()): number {
  return zonedMinutes(zone, instant) - zonedMinutes(GAME_TIME_ZONE, instant);
}

/**
 * Whether a reminder at this local time would arrive after the prompt rotates.
 *
 * True means the reminder is useless: by the time it fires, the day it is
 * reminding about is over.
 */
export function landsAfterCutoff(
  minuteOfDay: number,
  zone: string,
  instant: Date = new Date(),
): boolean {
  return minuteOfDay - minutesAheadOfPacific(zone, instant) >= MINUTES_PER_DAY;
}

/** `18:30` from 1110. The value a time input wants. */
export function toTimeInput(minuteOfDay: number): string {
  const hours = Math.floor(minuteOfDay / 60);
  const minutes = minuteOfDay % 60;
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
}

/** 1110 from `18:30`. Returns null for anything unparseable. */
export function fromTimeInput(value: string): number | null {
  const match = /^(\d{1,2}):(\d{2})$/.exec(value.trim());
  if (!match) return null;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;

  return hours * 60 + minutes;
}

/** `6:30 pm`, for prose rather than for an input. */
export function describeTime(minuteOfDay: number): string {
  const hours24 = Math.floor(minuteOfDay / 60);
  const minutes = minuteOfDay % 60;
  const suffix = hours24 < 12 ? 'am' : 'pm';
  const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12;

  return minutes === 0
    ? `${hours12}${suffix}`
    : `${hours12}:${String(minutes).padStart(2, '0')}${suffix}`;
}
